import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Hero } from "@/components/Hero";
import { HOMEPAGE_HERO_V2 } from "@/lib/feature-flags";
import { trackConversion } from "@/hooks/use-page-view";
import { pickHeroDiscovery } from "@/lib/hero-utils";
import { sizedImage } from "@/lib/asset-map";
import { useLocale } from "@/hooks/use-locale";
import { homepageCountCopy } from "@/lib/homepage-locales";

import { ContinueWatchingRail } from "@/components/ContinueWatchingRail";
import { NewsletterBand } from "@/components/NewsletterBand";

import { NetflixRow } from "@/components/netflix/NetflixRow";
import { NetflixCard } from "@/components/netflix/NetflixCard";
import { MoreInfoDialog } from "@/components/netflix/MoreInfoDialog";
import { SeasonCard } from "@/components/netflix/SeasonCard";
import { useMyList } from "@/hooks/use-my-list";
import { groupByVibe } from "@/lib/vibes";
import { dailySeed } from "@/lib/history";
import { whyMessageForDate } from "@/lib/why-here";

import {
  getHomepageContent,
  type CollectionSummary,
  type DBDiscovery,
  type HomepageContent,
  type TrendingItem,
} from "@/lib/content.functions";
import { usePageView } from "@/hooks/use-page-view";

type HomepageLoaderData = HomepageContent & {
  collections: CollectionSummary[];
  trending: TrendingItem[];
};

export const Route = createFileRoute("/")({
  head: ({ loaderData }) => {
    const data = loaderData as HomepageLoaderData | undefined;
    const hero = pickHeroDiscovery(data?.discoveries ?? [], data?.heroSlug ?? null);
    const heroImg = hero?.image_url ? sizedImage(hero.image_url, 640) : null;

    return {
      meta: [
        { title: "Smartasaurus - Real Science That Sounds Fake" },
        {
          name: "description",
          content:
            "Short, true discoveries you can read in two minutes: weird animals, deep ocean, dangerous nature, space and the human body. New ones every day.",
        },
        { property: "og:title", content: "Smartasaurus - Real Science That Sounds Fake" },
        {
          property: "og:description",
          content: "Two-minute reads that make you the person who actually knows why things happen.",
        },
        { property: "og:url", content: "https://smartasaurus.com/" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "Smartasaurus - Real Science That Sounds Fake" },
        {
          name: "twitter:description",
          content: "Two-minute reads that make you the person who actually knows why things happen.",
        },
      ],
      links: [
        { rel: "canonical", href: "https://smartasaurus.com/" },
        // Preload fonts to avoid FOUT/LCP delays for text
        {
          rel: "preload",
          href: "/assets/fonts/anton-latin.woff2",
          as: "font",
          type: "font/woff2",
          crossOrigin: "anonymous",
        },
        {
          rel: "preload",
          href: "/assets/fonts/inter-latin.woff2",
          as: "font",
          type: "font/woff2",
          crossOrigin: "anonymous",
        },
        // Preload the specific hero image that will be picked
        ...(heroImg
          ? [{ rel: "preload", href: heroImg, as: "image", fetchPriority: "high" as const }]
          : []),
      ],
    };
  },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["homepage"],
      queryFn: (): Promise<HomepageLoaderData> => getHomepageContent(),
      staleTime: 10 * 60 * 1000,
    }),
  component: Index,
});

/** Rounds down to a clean marketing figure, e.g. 329 -> "300+", 22 -> "20+". */
function roundDown(n: number, step = 50): string {
  if (!n || n < step) return String(n);
  return `${Math.floor(n / step) * step}+`;
}

function Index() {
  const data = Route.useLoaderData() as HomepageLoaderData;
  const [lang] = useLocale();
  const localeCopy = homepageCountCopy(lang);
  usePageView("home");
  const myList = useMyList();
  const [openDiscovery, setOpenDiscovery] = useState<DBDiscovery | null>(null);

  const {
    justPublished,
    instagramLinked,
    heroList,
    trendingDiscoveries,
    vibeRows,
    myListDiscoveries,
    byCat,
  } = useMemo(() => {
    const empty = {
      justPublished: [] as DBDiscovery[],
      instagramLinked: [] as DBDiscovery[],
      heroList: [] as DBDiscovery[],
      trendingDiscoveries: [] as DBDiscovery[],
      vibeRows: [] as ReturnType<typeof groupByVibe>,
      myListDiscoveries: [] as DBDiscovery[],
      byCat: new Map<string, string>(),
    };
    if (!data) return empty;
    const published = data.discoveries;
    if (published.length === 0) return empty;

    const hero = pickHeroDiscovery(published, data.heroSlug);

    const withImage = published.filter((d) => !!d.image_url);
    const recentByDate = withImage
      .slice()
      .sort(
        (a, b) =>
          +new Date(b.publish_at ?? b.created_at) - +new Date(a.publish_at ?? a.created_at),
      );

    // Daily seeded shuffle: same order for server and browser (no hydration
    // mismatch), different every day so the homepage never looks stale.
    const daySeed = Math.floor(Date.now() / 86_400_000);
    const shuffle = <T,>(arr: T[]): T[] => {
      const out = arr.slice();
      let s = daySeed;
      for (let i = out.length - 1; i > 0; i--) {
        s = (s * 1103515245 + 12345) % 2147483648;
        const j = s % (i + 1);
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    };

    // Hero rotates through a few featured discoveries; the first one is the
    // deterministic daily pick (and the only preloaded image).
    const heroList = hero
      ? [hero, ...shuffle(withImage.filter((d) => d.slug !== hero.slug)).slice(0, 3)]
      : [];
    const heroSlugs = new Set(heroList.map((d) => d.slug));

    const bySlug = new Map(published.map((d) => [d.slug, d] as const));
    const byCat = new Map((data.categories ?? []).map((c) => [c.id, c.label] as const));

    // Just Published is a literal freshness shelf. Hero placement must not hide
    // the newest release, and publication time wins over draft creation time.
    const justPublished = recentByDate.slice(0, 6);
    const jpSlugs = new Set(justPublished.map((d) => d.slug));

    // Reels - Instagram-linked, ranked by engagement
    const parseViews = (v: string | null) => {
      if (!v) return 0;
      const m = v.replace(/,/g, "").match(/([\d.]+)\s*([kKmM]?)/);
      if (!m) return 0;
      const n = parseFloat(m[1]);
      const suf = m[2].toLowerCase();
      return suf === "m" ? n * 1_000_000 : suf === "k" ? n * 1000 : n;
    };
    const dScore = (d: DBDiscovery) =>
      Number(d.reel_score ?? d.reel_views ?? 0) || parseViews(d.views_label);
    const igStrict = (data.viralReels?.length ? data.viralReels : published).filter(
      (d) => d.image_url && (!!d.hq_origin_reel_id || d.hq_source === "instagram"),
    );
    const uniqueReel = new Map<string, DBDiscovery>();
    for (const d of igStrict) {
      const existing = uniqueReel.get(d.slug);
      if (!existing || dScore(d) > dScore(existing)) uniqueReel.set(d.slug, d);
    }
    const instagramLinked = shuffle(
      [...uniqueReel.values()]
        .filter((d) => !heroSlugs.has(d.slug) && !jpSlugs.has(d.slug))
        .sort((a, b) => dScore(b) - dScore(a) || +new Date(b.created_at) - +new Date(a.created_at))
        .slice(0, 24),
    ).slice(0, 6);
    const igSlugs = new Set(instagramLinked.map((d) => d.slug));

    // Trending - hydrate from published so NetflixCard gets full data (motion, excerpt)
    const trendingHydrated = (data.trending ?? [])
      .map((t) => bySlug.get(t.slug))
      .filter((d): d is DBDiscovery => !!d)
      .filter((d) => !heroSlugs.has(d.slug) && !jpSlugs.has(d.slug) && !igSlugs.has(d.slug))
      .slice(0, 6);
    const trendingDiscoveries: DBDiscovery[] = trendingHydrated.length
      ? trendingHydrated
      : shuffle(
          recentByDate.filter((d) => !heroSlugs.has(d.slug) && !jpSlugs.has(d.slug) && !igSlugs.has(d.slug)),
        ).slice(0, 6);

    // Vibe subcategory rows - from the remaining pool (exclude everything shown above)
    const shownSlugs = new Set<string>([
      ...heroSlugs,
      ...jpSlugs,
      ...igSlugs,
      ...trendingDiscoveries.map((d) => d.slug),
    ]);
    const vibePool = shuffle(published.filter((d) => d.image_url && !shownSlugs.has(d.slug)));
    // Keep the homepage short: three themed shelves max, the rest lives on /discoveries.
    const vibeRows = groupByVibe(vibePool, { minItems: 4, maxPerRow: 6 }).slice(0, 2);

    // My List (hydrate saved slugs)
    const myListDiscoveries = myList.list
      .map((slug) => bySlug.get(slug))
      .filter((d): d is DBDiscovery => !!d);

    return {
      justPublished,
      instagramLinked,
      heroList,
      trendingDiscoveries,
      vibeRows,
      myListDiscoveries,
      byCat,
    };
  }, [data, myList.list]);

  const tagFor = (d: DBDiscovery) => byCat.get(d.category_id ?? "") ?? "Discovery";
  const heroSlug = heroList[0]?.slug;

  useEffect(() => {
    if (heroSlug) trackConversion("hero_impression", `/discovery/${heroSlug}`);
  }, [heroSlug]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content">
        {/* Hero */}
        {(() => {
          const slides = heroList.map((d) => ({
            slug: d.slug,
            title: d.title,
            teaser: d.excerpt ?? undefined,
            image: d.image_url ?? undefined,
          }));
          return <Hero slides={slides} autoplayMs={6000} />;
        })()}

        {/* What this is - one clear promise, rotating daily */}
        {(() => {
          const why = whyMessageForDate(new Date());
          return (
            <section className="mx-auto max-w-3xl px-4 py-14 text-center md:py-20">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-primary">
                · {why.eyebrow}
              </p>
              <h2 className="mt-4 font-display text-2xl uppercase leading-tight md:text-4xl">
                {why.headline}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground md:text-base">
                {why.body}
              </p>
              <p className="mx-auto mt-3 max-w-xl text-xs text-muted-foreground/80" data-no-translate>
                {localeCopy.discoveriesAcross(
                  roundDown(data?.totals?.discoveries ?? 0),
                  roundDown(data?.totals?.collections ?? data?.collections?.length ?? 0, 5),
                )}
              </p>
            </section>
          );
        })()}

        {/* My List - Netflix-style personal shelf (client only) */}
        {myListDiscoveries.length > 0 && (
          <NetflixRow eyebrow="· My list" title="Saved to watch">
            {myListDiscoveries.map((d) => (
              <NetflixCard
                key={d.id}
                d={d}
                tag={tagFor(d)}
                onMoreInfo={setOpenDiscovery}
                isSaved
                onToggleSave={myList.toggle}
              />
            ))}
          </NetflixRow>
        )}

        <ContinueWatchingRail discoveries={data?.discoveries ?? []} />

        {/* FROM THE REELS - Instagram bridge, promoted to hero-tier visibility */}
        {instagramLinked.length > 0 && (
          <NetflixRow
            eyebrow="· Straight from Instagram"
            title="From the reels"
            seeAllTo="/discoveries"
            seeAllSearch={{ filter: "from-reels" }}
          >
            {instagramLinked.map((d) => (
              <NetflixCard
                key={d.id}
                d={d}
                tag="Instagram-linked"
                onMoreInfo={setOpenDiscovery}
                isSaved={myList.has(d.slug)}
                onToggleSave={myList.toggle}
                widthClass="w-[82vw] max-w-[360px] sm:w-[340px] md:w-[400px]"
              />
            ))}
          </NetflixRow>
        )}

        {/* JUST PUBLISHED */}
        {justPublished.length > 0 && (
          <NetflixRow
            eyebrow="· New arrivals"
            title="Just published"
            seeAllTo="/discoveries"
            seeAllSearch={{ sort: "newest" }}
          >
            {justPublished.map((d) => (
              <NetflixCard
                key={d.id}
                d={d}
                tag={tagFor(d)}
                onMoreInfo={setOpenDiscovery}
                isSaved={myList.has(d.slug)}
                onToggleSave={myList.toggle}
              />
            ))}
          </NetflixRow>
        )}

        {/* SEASONS - Collections reframed as bingeable seasons */}
        {(data?.collections?.length ?? 0) > 0 && (
          <NetflixRow
            eyebrow="· Series"
            title="Themed series - start at episode one"
            seeAllTo="/collections"
          >
            {data.collections.slice(0, 6).map((c, i) => (
              <SeasonCard key={c.id} c={c} index={i} />
            ))}
          </NetflixRow>
        )}

        {/* TRENDING */}
        {trendingDiscoveries.length > 0 && (
          <NetflixRow
            eyebrow="· Trending now"
            title="What everyone's watching"
            seeAllTo="/discoveries"
            seeAllSearch={{ sort: "trending" }}
          >
            {trendingDiscoveries.map((d) => (
              <NetflixCard
                key={d.id}
                d={d}
                tag={tagFor(d)}
                onMoreInfo={setOpenDiscovery}
                isSaved={myList.has(d.slug)}
                onToggleSave={myList.toggle}
              />
            ))}
          </NetflixRow>
        )}

        {/* VIBE SUBCATEGORY ROWS - compresses the long tail into themed shelves */}
        {vibeRows.map((row) => (
          <NetflixRow key={row.key} eyebrow={row.eyebrow} title={row.label}>
            {row.items.map((d) => (
              <NetflixCard
                key={d.id}
                d={d}
                tag={tagFor(d)}
                onMoreInfo={setOpenDiscovery}
                isSaved={myList.has(d.slug)}
                onToggleSave={myList.toggle}
              />
            ))}
          </NetflixRow>
        ))}

        <NewsletterBand source="homepage" />
      </main>
      <SiteFooter />
      <MoreInfoDialog
        d={openDiscovery}
        tag={openDiscovery ? tagFor(openDiscovery) : undefined}
        isSaved={openDiscovery ? myList.has(openDiscovery.slug) : false}
        onToggleSave={myList.toggle}
        onClose={() => setOpenDiscovery(null)}
      />
    </div>
  );
}
